import { mediaUrl, buildButtons, SOCIAL_LABELS } from "@/lib/cartes";
import {
  Phone, Mail, Globe, MapPin, MessageCircle, MessageSquare, FileText, Link2,
  Clock, Facebook, Instagram, Linkedin, Youtube, Twitter, ExternalLink,
} from "lucide-react";

const ICONS = { Phone, Mail, Globe, MapPin, MessageCircle, MessageSquare, FileText, Link2 };
const SOCIAL_ICONS = { facebook: Facebook, instagram: Instagram, linkedin: Linkedin, youtube: Youtube, twitter: Twitter, x: Twitter };

/* Rendu universel d'une carte digitale (mobile-first). */
export default function CardRenderer({ card, onTrack, preview = false }) {
  // En mode aperçu (landing/démo), le nom n'est pas un <h1> pour éviter les doublons SEO
  const NameTag = preview ? "div" : "h1";
  const theme = card.theme || {};
  const colors = theme.colors || {};
  const fonts = theme.fonts || {};
  const layout = theme.layout || "classic";
  const idn = card.identity || {};
  const buttons = buildButtons(card);
  const name = idn.display_name || idn.org_name || "Votre nom";
  const title = card.card_type === "evenement" ? (idn.org_name || "") : idn.role_title;

  const track = (type, key) => { if (onTrack) onTrack(type, key); };

  const styleVars = {
    "--c-bg": colors.bg, "--c-surface": colors.surface, "--c-text": colors.text,
    "--c-title": colors.title, "--c-primary": colors.primary, "--c-secondary": colors.secondary,
    "--c-btn": colors.button, "--c-btn-text": colors.button_text,
    fontFamily: fonts.body || "Inter, sans-serif",
  };

  const Avatar = ({ size = 104, ring = true }) => {
    const src = mediaUrl(idn.photo_url || idn.logo_url);
    return (
      <div
        className="rounded-full overflow-hidden flex items-center justify-center shrink-0 bg-center bg-cover"
        style={{ width: size, height: size, border: ring ? `3px solid ${colors.surface}` : "none",
                 background: src ? undefined : colors.primary, boxShadow: "0 8px 24px rgba(0,0,0,.18)" }}
        data-testid="card-avatar"
      >
        {src ? <img src={src} alt={name} className="w-full h-full object-cover" />
             : <span style={{ color: colors.button_text, fontSize: size * 0.4, fontWeight: 800 }}>{name.charAt(0).toUpperCase()}</span>}
      </div>
    );
  };

  const Heading = ({ align = "center", onDark = false }) => (
    <div className={`${align === "center" ? "text-center items-center" : "text-left items-start"} flex flex-col`}>
      <NameTag className="font-bold leading-tight" data-testid="card-name"
          style={{ fontFamily: fonts.title, color: onDark ? "#fff" : colors.title, fontSize: "1.6rem" }}>{name}</NameTag>
      {title && <p className="mt-1 font-semibold" style={{ color: onDark ? "rgba(255,255,255,.9)" : colors.primary, fontSize: ".95rem" }}>{title}</p>}
      {idn.org_name && idn.org_name !== name && card.card_type !== "evenement" &&
        <p className="mt-0.5 opacity-80" style={{ color: onDark ? "#fff" : colors.text, fontSize: ".85rem" }}>{idn.org_name}</p>}
      {idn.slogan && <p className="mt-2 italic opacity-90" style={{ color: onDark ? "#fff" : colors.text, fontSize: ".85rem" }}>“{idn.slogan}”</p>}
    </div>
  );

  const Buttons = () => (
    buttons.length > 0 && (
      <div className="grid grid-cols-1 gap-2.5 mt-5" data-testid="card-buttons">
        {buttons.map((b) => {
          const Icon = ICONS[b.icon] || Link2;
          return (
            <a key={b.key} href={b.href} target={b.external ? "_blank" : undefined} rel="noreferrer"
               onClick={() => track("click", b.key)} data-testid={`card-btn-${b.key}`}
               className="flex items-center justify-center gap-2 py-3 px-4 rounded-xl font-semibold text-sm transition-transform active:scale-95 hover:brightness-110"
               style={{ background: colors.button, color: colors.button_text }}>
              <Icon size={17} /> {b.label}
            </a>
          );
        })}
      </div>
    )
  );

  const Socials = () => (
    (card.socials || []).filter((s) => s.url).length > 0 && (
      <div className="flex flex-wrap justify-center gap-2 mt-4" data-testid="card-socials">
        {(card.socials || []).filter((s) => s.url).map((s, i) => {
          const Icon = SOCIAL_ICONS[s.type] || ExternalLink;
          return (
            <a key={i} href={/^https?:/.test(s.url) ? s.url : `https://${s.url}`} target="_blank" rel="noreferrer"
               onClick={() => track("click", `social_${s.type}`)} title={SOCIAL_LABELS[s.type] || s.type}
               className="w-10 h-10 rounded-full flex items-center justify-center hover:brightness-110"
               style={{ background: colors.secondary, color: colors.title }}>
              <Icon size={18} />
            </a>
          );
        })}
      </div>
    )
  );

  const About = () => card.about && (
    <Section title="À propos">
      <p className="whitespace-pre-line leading-relaxed" style={{ color: colors.text, fontSize: ".9rem" }}>{card.about}</p>
    </Section>
  );

  const Section = ({ title: t, children }) => (
    <div className="mt-5 rounded-2xl p-4" style={{ background: colors.surface, border: `1px solid ${colors.secondary}40` }}>
      <h2 className="font-bold mb-2 uppercase tracking-wide" style={{ color: colors.title, fontFamily: fonts.title, fontSize: ".72rem", letterSpacing: ".08em" }}>{t}</h2>
      {children}
    </div>
  );

  const Contact = () => {
    const c = card.contact || {};
    const rows = [];
    if (c.phone) rows.push(["Phone", c.phone]);
    if (c.email) rows.push(["Mail", c.email]);
    if (c.website) rows.push(["Globe", c.website]);
    if (c.address && c.show_address) rows.push(["MapPin", c.address]);
    if (!rows.length) return null;
    return (
      <Section title="Coordonnées">
        <div className="space-y-2">
          {rows.map(([icon, val], i) => {
            const Icon = ICONS[icon];
            return (
              <div key={i} className="flex items-center gap-2.5" style={{ color: colors.text, fontSize: ".88rem" }}>
                <Icon size={16} style={{ color: colors.primary }} /> <span className="break-all">{val}</span>
              </div>
            );
          })}
          {card.hours && <div className="flex items-start gap-2.5 pt-1" style={{ color: colors.text, fontSize: ".85rem" }}><Clock size={16} style={{ color: colors.primary }} /><span className="whitespace-pre-line">{card.hours}</span></div>}
        </div>
      </Section>
    );
  };

  const Services = () => (card.services || []).filter((s) => s.title).length > 0 && (
    <Section title="Services & prestations">
      <div className="space-y-2.5">
        {(card.services || []).filter((s) => s.title).map((s, i) => (
          <div key={i} className="flex items-start justify-between gap-3">
            <div>
              <div className="font-semibold" style={{ color: colors.title, fontSize: ".88rem" }}>{s.title}</div>
              {s.desc && <div style={{ color: colors.text, fontSize: ".8rem" }} className="opacity-90">{s.desc}</div>}
            </div>
            {s.price && <div className="font-bold shrink-0" style={{ color: colors.primary, fontSize: ".85rem" }}>{s.price}</div>}
          </div>
        ))}
      </div>
    </Section>
  );

  const Team = () => (card.team || []).filter((m) => m.name).length > 0 && (
    <Section title="Équipe / Membres">
      <div className="grid grid-cols-2 gap-3">
        {(card.team || []).filter((m) => m.name).map((m, i) => (
          <div key={i} className="flex items-center gap-2">
            {m.photo_url ? <img src={mediaUrl(m.photo_url)} alt={m.name} className="w-9 h-9 rounded-full object-cover" />
              : <div className="w-9 h-9 rounded-full flex items-center justify-center font-bold" style={{ background: colors.secondary, color: colors.title }}>{m.name.charAt(0)}</div>}
            <div className="min-w-0">
              <div className="font-semibold truncate" style={{ color: colors.title, fontSize: ".8rem" }}>{m.name}</div>
              {m.role && <div className="truncate opacity-80" style={{ color: colors.text, fontSize: ".72rem" }}>{m.role}</div>}
            </div>
          </div>
        ))}
      </div>
    </Section>
  );

  const Gallery = () => (card.gallery || []).length > 0 && (
    <Section title="Galerie">
      <div className="grid grid-cols-3 gap-2">
        {(card.gallery || []).slice(0, 9).map((g, i) => (
          <img key={i} src={mediaUrl(g)} alt="" className="w-full aspect-square object-cover rounded-lg" />
        ))}
      </div>
    </Section>
  );

  const EventBlock = () => {
    const e = card.event || {};
    if (card.card_type !== "evenement" || !(e.date || e.place || e.program)) return null;
    return (
      <Section title="L'événement">
        <div className="space-y-1.5" style={{ color: colors.text, fontSize: ".88rem" }}>
          {e.date && <div><strong style={{ color: colors.title }}>Date :</strong> {e.date} {e.time}</div>}
          {e.place && <div><strong style={{ color: colors.title }}>Lieu :</strong> {e.place}</div>}
          {e.program && <div className="whitespace-pre-line pt-1">{e.program}</div>}
        </div>
      </Section>
    );
  };

  const Body = () => (
    <>
      <About />
      <EventBlock />
      <Services />
      <Gallery />
      <Team />
      <Contact />
    </>
  );

  const cover = mediaUrl(idn.cover_url);

  /* ---------- Variantes de structure ---------- */
  let header;
  if (layout === "cover" || layout === "showcase") {
    header = (
      <div>
        <div className="h-36 bg-center bg-cover relative" style={{ background: cover ? `url(${cover})` : colors.primary }}>
          <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, transparent, rgba(0,0,0,.25))" }} />
        </div>
        <div className="px-5 -mt-12 relative"><Avatar size={92} /><div className="mt-3"><Heading align="left" /></div></div>
      </div>
    );
  } else if (layout === "split" || layout === "bold") {
    header = (
      <div>
        <div className="px-5 pt-8 pb-6 text-center" style={{ background: colors.primary }}>
          <div className="flex justify-center mb-3"><Avatar size={layout === "bold" ? 96 : 84} /></div>
          <Heading align="center" onDark />
        </div>
      </div>
    );
  } else if (layout === "gradient" || layout === "premium") {
    header = (
      <div className="px-5 pt-9 pb-6 text-center"
           style={{ background: `linear-gradient(160deg, ${colors.primary}, ${colors.bg})` }}>
        <div className="flex justify-center mb-3"><Avatar size={96} /></div>
        <Heading align="center" onDark={!!colors.dark} />
      </div>
    );
  } else if (layout === "poster") {
    header = (
      <div className="px-5 pt-10 pb-7 text-center" style={{ background: colors.primary }}>
        {cover && <img src={cover} alt="" className="w-28 h-28 mx-auto rounded-xl object-cover mb-3" />}
        <NameTag className="font-extrabold leading-tight" style={{ fontFamily: fonts.title, color: "#fff", fontSize: "1.9rem" }}>{name}</NameTag>
        {card.event?.date && <p className="mt-2 font-semibold" style={{ color: colors.secondary }}>{card.event.date} {card.event.time}</p>}
        {idn.slogan && <p className="mt-1 italic text-white/90 text-sm">“{idn.slogan}”</p>}
      </div>
    );
  } else if (layout === "minimal" || layout === "sidebar") {
    header = (
      <div className="px-6 pt-9 pb-2" style={{ borderLeft: layout === "sidebar" ? `5px solid ${colors.primary}` : "none" }}>
        <Avatar size={78} ring={false} />
        <div className="mt-4"><Heading align="left" /></div>
      </div>
    );
  } else { // classic
    header = (
      <div className="px-5 pt-9 pb-2 text-center">
        <div className="flex justify-center mb-3"><Avatar size={100} /></div>
        <Heading align="center" />
      </div>
    );
  }

  return (
    <div data-testid="card-renderer" className="w-full min-h-full" style={{ ...styleVars, background: colors.bg, color: colors.text }}>
      {header}
      <div className="px-5 pb-8">
        <Buttons />
        <Socials />
        <Body />
      </div>
    </div>
  );
}
